import { useState } from 'react';
import clsx from 'clsx';
import type {
  Channel,
  Discipline,
  Level,
  Model,
  LevelId,
} from '@rc/engine/types';
import SpendPerFteTable from './model/SpendPerFteTable';
import LevelWeightsMatrix from './model/LevelWeightsMatrix';
import CostPerRoleMatrix from './model/CostPerRoleMatrix';
import OffshorePctMatrix from './model/OffshorePctMatrix';
import BucketsHubsEditor from './model/BucketsHubsEditor';
import TimeConcentrationEditor from './model/TimeConcentrationEditor';
import AutomationImpactsEditor from './model/AutomationImpactsEditor';
import ProfileFactorsEditor from './model/ProfileFactorsEditor';
import SavedPresetsEditor from './model/SavedPresetsEditor';
import CurrencyEditor from './model/CurrencyEditor';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

const TABS: { id: TabId; label: string }[] = [
  { id: 'disciplines', label: 'Disciplines' },
  { id: 'channels', label: 'Channels' },
  { id: 'levels', label: 'Levels' },
  { id: 'profile_factors', label: 'Profile factors' },
  { id: 'spend_per_fte', label: 'Spend per FTE' },
  { id: 'level_weights', label: 'Level weights' },
  { id: 'cost_per_role', label: 'Cost per role' },
  { id: 'offshore_pct', label: 'Hub locations %' },
  { id: 'saved_presets', label: 'Saved presets' },
  { id: 'buckets_hubs', label: 'Buckets & hubs' },
  { id: 'time_concentration', label: 'Time concentration' },
  { id: 'automation_impacts', label: 'Automation impacts' },
  { id: 'currency', label: 'Currency' },
];

type TabId =
  | 'channels'
  | 'disciplines'
  | 'levels'
  | 'profile_factors'
  | 'spend_per_fte'
  | 'level_weights'
  | 'cost_per_role'
  | 'offshore_pct'
  | 'saved_presets'
  | 'buckets_hubs'
  | 'time_concentration'
  | 'automation_impacts'
  | 'currency';

export default function ModelEditor({ model, onChange }: Props) {
  const [tab, setTab] = useState<TabId>('spend_per_fte');

  return (
    <div className="card">
      <div className="border-b border-slate-200 flex flex-wrap gap-1 px-4 pt-3">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-3.5 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px',
              tab === t.id
                ? 'text-brand-purple-700 border-brand-purple-500 bg-brand-purple-50/40'
                : 'text-slate-500 border-transparent hover:text-slate-800',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-6 space-y-4">
        {tab === 'channels' && <ChannelsTab model={model} onChange={onChange} />}
        {tab === 'disciplines' && <DisciplinesTab model={model} onChange={onChange} />}
        {tab === 'levels' && <LevelsTab model={model} onChange={onChange} />}
        {tab === 'profile_factors' && <ProfileFactorsEditor model={model} onChange={onChange} />}
        {tab === 'spend_per_fte' && <SpendPerFteTable model={model} onChange={onChange} />}
        {tab === 'level_weights' && <LevelWeightsMatrix model={model} onChange={onChange} />}
        {tab === 'cost_per_role' && <CostPerRoleMatrix model={model} onChange={onChange} />}
        {tab === 'offshore_pct' && <OffshorePctMatrix model={model} onChange={onChange} />}
        {tab === 'saved_presets' && <SavedPresetsEditor model={model} onChange={onChange} />}
        {tab === 'buckets_hubs' && <BucketsHubsEditor model={model} onChange={onChange} />}
        {tab === 'time_concentration' && (
          <TimeConcentrationEditor model={model} onChange={onChange} />
        )}
        {tab === 'automation_impacts' && (
          <AutomationImpactsEditor model={model} onChange={onChange} />
        )}
        {tab === 'currency' && <CurrencyEditor model={model} onChange={onChange} />}
      </div>
    </div>
  );
}

function ChannelsTab({ model, onChange }: Props) {
  const update = (channels: Channel[]) => onChange({ ...model, channels });
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left py-2 pr-2">Label</th>
            <th className="text-left py-2 pr-2">ID</th>
            <th className="text-left py-2 pr-2">Category</th>
            <th className="text-center py-2 pr-2">AdOps?</th>
            <th className="text-center py-2 pr-2">Active</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {model.channels.map((c, i) => (
            <tr key={c.id} className="border-t border-slate-100">
              <td className="py-1 pr-2">
                <input
                  className="input py-1 px-2"
                  value={c.label}
                  onChange={(e) => {
                    const next = [...model.channels];
                    next[i] = { ...c, label: e.target.value };
                    update(next);
                  }}
                />
              </td>
              <td className="py-1 pr-2 text-xs font-mono text-slate-500">{c.id}</td>
              <td className="py-1 pr-2">
                <select
                  className="input py-1 px-2"
                  value={c.category}
                  onChange={(e) => {
                    const next = [...model.channels];
                    next[i] = { ...c, category: e.target.value as Channel['category'] };
                    update(next);
                  }}
                >
                  <option value="traditional">Traditional</option>
                  <option value="addressable">Addressable</option>
                </select>
              </td>
              <td className="py-1 pr-2 text-center">
                <input
                  type="checkbox"
                  checked={!!c.adopsApplicable}
                  onChange={(e) => {
                    const next = [...model.channels];
                    next[i] = { ...c, adopsApplicable: e.target.checked };
                    update(next);
                  }}
                />
              </td>
              <td className="py-1 pr-2 text-center">
                <input
                  type="checkbox"
                  checked={c.active !== false}
                  onChange={(e) => {
                    const next = [...model.channels];
                    next[i] = { ...c, active: e.target.checked };
                    update(next);
                  }}
                />
              </td>
              <td className="py-1 text-right">
                <button
                  className="btn-ghost text-xs"
                  onClick={() => update(model.channels.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-slate-400 mt-3">
        Channel mix for new scenarios is seeded from <strong>channelMixPresets</strong> or split
        evenly; per-channel allocation lives on each scenario, not the model.
      </p>
      <button
        className="btn-secondary mt-3"
        onClick={() => {
          const id = prompt('New channel id (slug)');
          if (!id) return;
          update([
            ...model.channels,
            {
              id,
              label: id,
              category: 'addressable',
              active: true,
            },
          ]);
        }}
      >
        + Add channel
      </button>
    </div>
  );
}

function DisciplinesTab({ model, onChange }: Props) {
  const update = (disciplines: Discipline[]) => onChange({ ...model, disciplines });
  const allLevels = model.levels.map((l) => l.id);
  const setField = (i: number, patch: Partial<Discipline>) => {
    const next = [...model.disciplines];
    next[i] = { ...next[i], ...patch };
    update(next);
  };
  return (
    <div className="space-y-3">
      {model.disciplines.map((d, i) => (
        <div key={d.id} className="border border-slate-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="label">Label</label>
              <input
                className="input"
                value={d.label}
                onChange={(e) => setField(i, { label: e.target.value })}
              />
            </div>
            <div>
              <label className="label">ID</label>
              <input className="input bg-slate-50 text-slate-500" value={d.id} disabled />
            </div>
            <div>
              <label className="label">Active</label>
              <input
                type="checkbox"
                checked={d.active !== false}
                onChange={(e) => setField(i, { active: e.target.checked })}
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="label">Active levels</label>
            <div className="flex flex-wrap gap-1.5">
              {allLevels.map((l) => {
                const on = (d.activeLevels || ['L1', 'L2', 'L3', 'L4', 'L5']).includes(l);
                return (
                  <button
                    key={l}
                    onClick={() => {
                      const cur = d.activeLevels || ['L1', 'L2', 'L3', 'L4', 'L5'];
                      const next = on
                        ? cur.filter((x) => x !== l)
                        : ([...cur, l].sort() as LevelId[]);
                      setField(i, { activeLevels: next as LevelId[] });
                    }}
                    className={clsx(
                      'pill border transition-colors',
                      on
                        ? 'bg-brand-purple-100 text-brand-purple-700 border-brand-purple-200'
                        : 'bg-white text-slate-500 border-slate-200',
                    )}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Per-level distribution is driven by <strong>Level weights</strong>.
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function LevelsTab({ model, onChange }: Props) {
  const update = (levels: Level[]) => onChange({ ...model, levels });
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left py-2 pr-2">Label</th>
            <th className="text-left py-2 pr-2">ID</th>
            <th className="text-left py-2 pr-2">Description</th>
            <th className="text-center py-2 pr-2">Default active</th>
          </tr>
        </thead>
        <tbody>
          {model.levels.map((l, i) => (
            <tr key={l.id} className="border-t border-slate-100">
              <td className="py-1 pr-2">
                <input
                  className="input py-1 px-2"
                  value={l.label}
                  onChange={(e) => {
                    const next = [...model.levels];
                    next[i] = { ...l, label: e.target.value };
                    update(next);
                  }}
                />
              </td>
              <td className="py-1 pr-2 text-xs font-mono text-slate-500">{l.id}</td>
              <td className="py-1 pr-2">
                <input
                  className="input py-1 px-2"
                  value={l.description || ''}
                  placeholder="(optional)"
                  onChange={(e) => {
                    const next = [...model.levels];
                    next[i] = { ...l, description: e.target.value };
                    update(next);
                  }}
                />
              </td>
              <td className="py-1 pr-2 text-center">
                <input
                  type="checkbox"
                  checked={l.defaultActive !== false}
                  onChange={(e) => {
                    const next = [...model.levels];
                    next[i] = { ...l, defaultActive: e.target.checked };
                    update(next);
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-slate-400 mt-3">
        Per-cell costs come from the <strong>Cost per role</strong> table. Automation hour
        savings are configured in the <strong>Automation Impact</strong> catalog and applied
        on top of the time-concentration heatmap in the output view.
      </p>
    </div>
  );
}

